import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import ApiInspector from '../../../components/ApiInspector';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import { API_DOCS } from './NominativiPage.api';


// SVG Icons
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);
const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path>
  </svg>
);
const UsersIcon = ({ size = 48 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle>
  </svg>
);

interface Nominativo {
  id: number;
  codice: string;
  tipoSoggetto: 'AZIENDA' | 'PRIVATO';
  ragioneSociale?: string;
  nome?: string;
  cognome?: string;
  partitaIva?: string;
  isCliente: boolean;
  isFornitore: boolean;
  disattivato: boolean;
  bloccato: boolean;
  cliente?: {
    categoriaCliente: string;
    accountId?: number;
  };
  fornitore?: {
    categoriaFornitore: string;
  };
  indirizzi?: Array<{
    tipo: string;
    citta?: string;
  }>;
  societa?: {
    id: number;
    nome: string;
  };
}

interface Utente {
  id: number;
  nome: string;
}

interface Insegna {
  id: number;
  nome: string;
  codice: string;
}

interface FiltriAttivi {
  [key: string]: string;
}

export default function NominativiPage() {
  const navigate = useNavigate();
  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isDisabled: societaDisabled,
    pronta,
  } = useFiltroSocieta();

  console.log('[NominativiPage] Render - filterSocietaId:', filterSocietaId, 'societaDisabled:', societaDisabled, 'pronta:', pronta);

  const [nominativi, setNominativi] = useState<Nominativo[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [multiSocieta, setMultiSocieta] = useState(false);

  // Filtri
  const [q, setQ] = useState('');
  const [ruolo, setRuolo] = useState('');
  const [categoriaCliente, setCategoriaCliente] = useState('');
  const [categoriaFornitore, setCategoriaFornitore] = useState('');
  const [provincia, setProvincia] = useState('');
  const [citta, setCitta] = useState('');
  const [accountId, setAccountId] = useState('');
  const [insegnaClienteId, setInsegnaClienteId] = useState('');
  const [stato, setStato] = useState('ATTIVI');
  const [conPartitaIva, setConPartitaIva] = useState('');
  const [dal, setDal] = useState('');
  const [al, setAl] = useState('');

  // Liste select
  const [utenti, setUtenti] = useState<Utente[]>([]);
  const [insegne, setInsegne] = useState<Insegna[]>([]);

  // Filtri attivi (per i tag)
  const [filtriAttivi, setFiltriAttivi] = useState<FiltriAttivi>({});

  // Modal selezione società per nuovo nominativo
  const [showSocietaModal, setShowSocietaModal] = useState(false);
  const [selectedSocietaForNew, setSelectedSocietaForNew] = useState('');

  const loadUtenti = useCallback(async () => {
    if (!filterSocietaId) return;
    try {
      // @api GET /api/utenti/selezionabili?societaId=N -> {"success":"true","data":[{"id":"number","nome":"string"}]}
      const response = await apiFetch<{ success: boolean; data: Utente[] }>(`/api/utenti/selezionabili?societaId=${filterSocietaId}`);
      setUtenti(response.data || []);
    } catch (err) {
      console.error('Errore caricamento utenti:', err);
      setUtenti([]);
    }
  }, [filterSocietaId]);

  const loadInsegne = useCallback(async () => {
    if (!filterSocietaId) return;
    try {
      // @api GET /api/anagrafiche/insegne -> [{"id":"number","nome":"string","codice":"string"}]
      const data = await apiFetch<Insegna[]>(`/api/anagrafiche/insegne?societaId=${filterSocietaId}`);
      setInsegne(data);
    } catch (err) {
      console.error('Errore caricamento insegne:', err);
    }
  }, [filterSocietaId]);

  const loadNominativi = useCallback(async () => {
    if (!pronta) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      // Appendi societaId SOLO se non è vuoto
      if (filterSocietaId) {
        params.append('societaId', filterSocietaId);
      }
      if (q) params.append('q', q);
      if (ruolo) params.append('ruolo', ruolo);
      if (categoriaCliente) params.append('categoriaCliente', categoriaCliente);
      if (categoriaFornitore) params.append('categoriaFornitore', categoriaFornitore);
      if (provincia) params.append('provincia', provincia.toUpperCase());
      if (citta) params.append('citta', citta);
      if (accountId) params.append('accountId', accountId);
      if (insegnaClienteId) params.append('insegnaClienteId', insegnaClienteId);
      if (stato) params.append('stato', stato);
      if (conPartitaIva) params.append('conPartitaIva', conPartitaIva);
      if (dal) params.append('dal', dal);
      if (al) params.append('al', al);
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      // @api GET /api/nominativi -> {"data":[{"id":"number","codice":"string","tipoSoggetto":"string","ragioneSociale":"string","isCliente":"boolean","isFornitore":"boolean","disattivato":"boolean","bloccato":"boolean"}],"total":"number","multiSocieta":"boolean"}
      const response = await apiFetch<{ data: Nominativo[]; total: number; multiSocieta?: boolean }>(`/api/nominativi?${params}`);
      setNominativi(response.data);
      setTotal(response.total);
      setMultiSocieta(response.multiSocieta || false);
    } catch (err) {
      console.error('Errore caricamento nominativi:', err);
    } finally {
      setLoading(false);
    }
  }, [pronta, filterSocietaId, q, ruolo, categoriaCliente, categoriaFornitore, provincia, citta, accountId, insegnaClienteId, stato, conPartitaIva, dal, al, page, limit]);

  useEffect(() => {
    if (filterSocietaId) {
      loadUtenti();
      loadInsegne();
    }
  }, [filterSocietaId, loadUtenti, loadInsegne]);

  useEffect(() => {
    loadNominativi();
  }, [loadNominativi]);

  const handleCerca = () => {
    setPage(1);
    const attivi: FiltriAttivi = {};
    if (q) attivi['Ricerca'] = q;
    if (ruolo) attivi['Ruolo'] = ruolo === 'CLIENTE' ? 'Solo clienti' : ruolo === 'FORNITORE' ? 'Solo fornitori' : 'Entrambi';
    if (categoriaCliente) attivi['Categoria cliente'] = categoriaCliente;
    if (categoriaFornitore) attivi['Categoria fornitore'] = categoriaFornitore;
    if (provincia) attivi['Provincia'] = provincia;
    if (citta) attivi['Città'] = citta;
    if (accountId) {
      const utente = utenti.find(u => u.id === parseInt(accountId));
      if (utente) attivi['Account'] = utente.nome;
    }
    if (insegnaClienteId) {
      const ins = insegne.find(i => i.id === parseInt(insegnaClienteId));
      if (ins) attivi['Insegna'] = ins.nome;
    }
    if (stato && stato !== 'ATTIVI') attivi['Stato'] = stato;
    if (conPartitaIva) attivi['P.IVA'] = conPartitaIva === 'true' ? 'Con P.IVA' : 'Senza P.IVA';
    if (dal) attivi['Dal'] = dal;
    if (al) attivi['Al'] = al;
    setFiltriAttivi(attivi);
    loadNominativi();
  };

  const handleAzzeraFiltri = () => {
    setQ('');
    setRuolo('');
    setCategoriaCliente('');
    setCategoriaFornitore('');
    setProvincia('');
    setCitta('');
    setAccountId('');
    setInsegnaClienteId('');
    setStato('ATTIVI');
    setConPartitaIva('');
    setDal('');
    setAl('');
    setPage(1);
    setFiltriAttivi({});
  };

  const rimuoviFiltro = (key: string) => {
    const newFiltri = { ...filtriAttivi };
    delete newFiltri[key];
    setFiltriAttivi(newFiltri);

    if (key === 'Ricerca') setQ('');
    else if (key === 'Ruolo') setRuolo('');
    else if (key === 'Categoria cliente') setCategoriaCliente('');
    else if (key === 'Categoria fornitore') setCategoriaFornitore('');
    else if (key === 'Provincia') setProvincia('');
    else if (key === 'Città') setCitta('');
    else if (key === 'Account') setAccountId('');
    else if (key === 'Insegna') setInsegnaClienteId('');
    else if (key === 'Stato') setStato('ATTIVI');
    else if (key === 'P.IVA') setConPartitaIva('');
    else if (key === 'Dal') setDal('');
    else if (key === 'Al') setAl('');

    setPage(1);
  };

  const handleNuovo = () => {
    if (filterSocietaId) {
      navigate(`/anagrafiche/nominativi/nuovo?societaId=${filterSocietaId}`);
    } else {
      setSelectedSocietaForNew('');
      setShowSocietaModal(true);
    }
  };

  const handleContinuaNuovo = () => {
    if (selectedSocietaForNew) {
      setShowSocietaModal(false);
      setSelectedSocietaForNew('');
      navigate(`/anagrafiche/nominativi/nuovo?societaId=${selectedSocietaForNew}`);
    }
  };

  const handleEsporta = () => {
    alert('Funzionalità in arrivo');
  };

  const handleRowClick = (id: number) => {
    navigate(`/anagrafiche/nominativi/${id}`);
  };

  const getNomeCompleto = (nom: Nominativo) => {
    if (nom.tipoSoggetto === 'AZIENDA') return nom.ragioneSociale || '—';
    return `${nom.nome || ''} ${nom.cognome || ''}`.trim() || '—';
  };

  const getRuoloBadge = (nom: Nominativo) => {
    const badges: React.ReactElement[] = [];
    if (nom.isCliente) {
      badges.push(
        <span key="cliente" className="bop-badge bop-badge--info bop-nominativi__badge-pill">
          Cliente
        </span>
      );
    }
    if (nom.isFornitore) {
      badges.push(
        <span key="fornitore" className="bop-badge bop-nominativi__badge-pill bop-nominativi__badge--fornitore">
          Fornitore
        </span>
      );
    }
    return badges.length > 0 ? <>{badges}</> : <span className="bop-text-muted">—</span>;
  };

  const getCategoriaBadge = (nom: Nominativo) => {
    const badges: React.ReactElement[] = [];
    if (nom.cliente?.categoriaCliente) {
      badges.push(
        <span key="cat-cliente" className="bop-badge bop-nominativi__badge-pill bop-nominativi__badge--cat-cliente">
          {nom.cliente.categoriaCliente.replace('_', ' ')}
        </span>
      );
    }
    if (nom.fornitore?.categoriaFornitore) {
      badges.push(
        <span key="cat-fornitore" className="bop-badge bop-nominativi__badge-pill bop-nominativi__badge--cat-fornitore">
          {nom.fornitore.categoriaFornitore.replace('_', ' ')}
        </span>
      );
    }
    return badges.length > 0 ? <>{badges}</> : <span className="bop-text-muted">—</span>;
  };

  const getStatoBadge = (nom: Nominativo) => {
    if (nom.disattivato) {
      return <span className="bop-badge bop-badge--neutral bop-nominativi__badge-pill">Disattivato</span>;
    }
    if (nom.bloccato) {
      return <span className="bop-badge bop-badge--warning bop-nominativi__badge-pill">Bloccato</span>;
    }
    return <span className="bop-badge bop-badge--success bop-nominativi__badge-pill">Attivo</span>;
  };

  const totalPages = Math.ceil(total / limit);

  const columns = useMemo<BopExcelColumn<Nominativo>[]>(() => {
    const base: BopExcelColumn<Nominativo>[] = [
      {
        key: 'codice',
        header: 'Codice',
        accessor: (n) => n.codice,
        filter: { type: 'text' },
        width: '120px',
        cellClassName: 'bop-nominativi__td-codice',
        render: (n) => n.codice,
      },
      {
        key: 'nome',
        header: 'Ragione sociale / Nome',
        accessor: (n) => getNomeCompleto(n),
        filter: { type: 'text' },
        cellClassName: 'bop-nominativi__td-nome',
        render: (n) => getNomeCompleto(n),
      },
      {
        key: 'partitaIva',
        header: 'P.IVA',
        accessor: (n) => n.partitaIva || '',
        filter: { type: 'text' },
        width: '130px',
        cellClassName: 'bop-nominativi__td-piva',
        render: (n) => n.partitaIva || '—',
      },
      {
        key: 'ruolo',
        header: 'Ruolo',
        accessor: (n) => `${n.isCliente ? 'CLIENTE' : ''} ${n.isFornitore ? 'FORNITORE' : ''}`.trim(),
        filter: {
          type: 'enum',
          options: [
            { value: 'CLIENTE',   label: 'Cliente' },
            { value: 'FORNITORE', label: 'Fornitore' },
          ],
        },
        width: '160px',
        render: (n) => getRuoloBadge(n),
      },
      {
        key: 'categoria',
        header: 'Categoria',
        accessor: (n) => `${n.cliente?.categoriaCliente || ''} ${n.fornitore?.categoriaFornitore || ''}`.trim(),
        filter: { type: 'text' },
        width: '180px',
        render: (n) => getCategoriaBadge(n),
      },
    ];
    if (multiSocieta) {
      base.push({
        key: 'societa',
        header: 'Società',
        accessor: (n) => n.societa?.nome || '',
        filter: { type: 'text' },
        width: '150px',
        cellClassName: 'bop-nominativi__td-societa',
        render: (n) => n.societa?.nome || '—',
      });
    }
    base.push(
      {
        key: 'citta',
        header: 'Città',
        accessor: (n) => n.indirizzi?.find(i => i.tipo === 'SEDE_LEGALE')?.citta || '',
        filter: { type: 'text' },
        width: '140px',
        cellClassName: 'bop-nominativi__td-citta',
        render: (n) => n.indirizzi?.find(i => i.tipo === 'SEDE_LEGALE')?.citta || '—',
      },
      {
        key: 'stato',
        header: 'Stato',
        accessor: (n) => n.disattivato ? 'DISATTIVATO' : n.bloccato ? 'BLOCCATO' : 'ATTIVO',
        filter: {
          type: 'enum',
          options: [
            { value: 'ATTIVO',      label: 'Attivo' },
            { value: 'BLOCCATO',    label: 'Bloccato' },
            { value: 'DISATTIVATO', label: 'Disattivato' },
          ],
        },
        width: '120px',
        render: (n) => getStatoBadge(n),
      },
    );
    return base;
  }, [multiSocieta]);

  if (!pronta) {
    return (
      <Layout>
        <div className="bop-nominativi__loading">
          <p className="bop-text-muted">Caricamento...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-page-header">
        <div>
          <h1 className="bop-page-title">Nominativi</h1>
          <p className="bop-nominativi__subtitle">Registro anagrafico clienti e fornitori</p>
        </div>
        <div className="bop-flex">
          <button className="bop-btn bop-btn-secondary" onClick={handleEsporta}>
            <DownloadIcon />
            Esporta
          </button>
          <button className="bop-btn bop-btn-primary" onClick={handleNuovo}>
            <PlusIcon />
            Nuovo nominativo
          </button>
        </div>
      </div>

      {/* Pannello filtri */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-nominativi__filter-title">Filtri di ricerca</h3>

        {/* Riga società (visibile solo quando topbar = "Tutte le società") */}
        {!societaDisabled && (
          <div className="bop-nominativi__filter-row">
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={filterSocietaId}
                onChange={(e) => setFilterSocietaId(e.target.value)}
              >
                <option value="">Seleziona società...</option>
                {societaList.map((soc) => (
                  <option key={soc.id} value={soc.id}>
                    {soc.nome}
                  </option>
                ))}
              </select>
            </div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        )}

        {/* Prima riga filtri */}
        <div className="bop-nominativi__filter-row">
          <div className="bop-form-group">
            <label className="bop-label">Ricerca testo libero</label>
            <input
              type="text"
              className="bop-input"
              placeholder="Ragione sociale, nome, codice, P.IVA..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Ruolo</label>
            <select className="bop-select" value={ruolo} onChange={(e) => setRuolo(e.target.value)}>
              <option value="">Tutti</option>
              <option value="CLIENTE">Solo clienti</option>
              <option value="FORNITORE">Solo fornitori</option>
              <option value="ENTRAMBI">Entrambi</option>
            </select>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Categoria cliente</label>
            <select className="bop-select" value={categoriaCliente} onChange={(e) => setCategoriaCliente(e.target.value)}>
              <option value="">Tutte</option>
              <option value="CATENA_GDO">Catena GDO</option>
              <option value="AZIENDA_B2B">Azienda B2B</option>
              <option value="PRIVATO_B2C">Privato B2C</option>
              <option value="AGENTE">Agente</option>
            </select>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Categoria fornitore</label>
            <select className="bop-select" value={categoriaFornitore} onChange={(e) => setCategoriaFornitore(e.target.value)}>
              <option value="">Tutte</option>
              <option value="FORNITORE_MERCE">Fornitore merce</option>
              <option value="SUBAPPALTATORE">Subappaltatore</option>
              <option value="FORNITORE_SERVIZI">Fornitore servizi</option>
              <option value="AGENTE">Agente</option>
            </select>
          </div>
        </div>

        {/* Seconda riga */}
        <div className="bop-nominativi__filter-row">
          <div className="bop-form-group">
            <label className="bop-label">Provincia (2 lettere)</label>
            <input
              type="text"
              className="bop-input"
              placeholder="es. MI"
              maxLength={2}
              value={provincia}
              onChange={(e) => setProvincia(e.target.value.toUpperCase())}
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Città</label>
            <input
              type="text"
              className="bop-input"
              placeholder="es. Milano"
              value={citta}
              onChange={(e) => setCitta(e.target.value)}
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Account assegnato</label>
            <select className="bop-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">Tutti</option>
              {utenti.map((u) => (
                <option key={u.id} value={u.id}>{u.nome}</option>
              ))}
            </select>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Insegna</label>
            <select className="bop-select" value={insegnaClienteId} onChange={(e) => setInsegnaClienteId(e.target.value)}>
              <option value="">Tutte</option>
              {insegne.map((i) => (
                <option key={i.id} value={i.id}>{i.nome}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Terza riga */}
        <div className="bop-nominativi__filter-row">
          <div className="bop-form-group">
            <label className="bop-label">Stato</label>
            <select className="bop-select" value={stato} onChange={(e) => setStato(e.target.value)}>
              <option value="ATTIVI">Attivi</option>
              <option value="DISATTIVATI">Disattivati</option>
              <option value="BLOCCATI">Bloccati</option>
              <option value="TUTTI">Tutti</option>
            </select>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Partita IVA</label>
            <select className="bop-select" value={conPartitaIva} onChange={(e) => setConPartitaIva(e.target.value)}>
              <option value="">Tutti</option>
              <option value="true">Con P.IVA</option>
              <option value="false">Senza P.IVA</option>
            </select>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Creato dal</label>
            <input
              type="date"
              className="bop-input"
              value={dal}
              onChange={(e) => setDal(e.target.value)}
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Creato al</label>
            <input
              type="date"
              className="bop-input"
              value={al}
              onChange={(e) => setAl(e.target.value)}
            />
          </div>
        </div>

        <div className="bop-flex">
          <button className="bop-btn bop-btn-secondary" onClick={handleAzzeraFiltri}>Azzera filtri</button>
          <button className="bop-btn bop-btn-primary" onClick={handleCerca}>
            <SearchIcon />
            Cerca
          </button>
        </div>
      </div>

      {/* Filtri attivi */}
      {Object.keys(filtriAttivi).length > 0 && (
        <div className="bop-nominativi__active-filters">
          {Object.entries(filtriAttivi).map(([key, value]) => (
            <div key={key} className="bop-nominativi__filter-tag">
              <strong className="bop-nominativi__filter-tag-key">{key}:</strong>
              <span className="bop-nominativi__filter-tag-value">{value}</span>
              <button
                className="bop-nominativi__filter-tag-remove"
                onClick={() => rimuoviFiltro(key)}
              >
                <XIcon />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Tabella Excel-like (paginazione server-side gestita esternamente sotto) */}
      <BopExcelTable<Nominativo>
        data={nominativi}
        loading={loading}
        rowKey={(n) => n.id}
        onRowClick={(n) => handleRowClick(n.id)}
        hidePagination
        emptyState={
          <div className="bop-empty-state">
            <UsersIcon size={48} />
            <p className="bop-empty-state__text">Nessun nominativo trovato</p>
          </div>
        }
        columns={columns}
      />

      {/* Paginazione server-side (la lista è caricata pagina per pagina dal backend) */}
      {!loading && nominativi.length > 0 && (
        <div className="bop-pagination">
          <div className="bop-pagination__info">
            Pagina {page} di {totalPages} — {total} risultati
          </div>

          <div className="bop-pagination__controls">
            <label className="bop-nominativi__page-size-label">Righe per pagina:</label>
            <select
              className="bop-select bop-nominativi__page-size-select"
              value={limit}
              onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>

            <button
              className="bop-btn bop-btn-secondary bop-btn-sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Prec
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5 && page > 3) {
                pageNum = page - 2 + i;
                if (pageNum > totalPages) pageNum = totalPages - (4 - i);
              }
              return (
                <button
                  key={pageNum}
                  className={`bop-btn bop-btn-sm ${page === pageNum ? 'bop-btn-primary' : 'bop-btn-secondary'}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              className="bop-btn bop-btn-secondary bop-btn-sm"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Succ
            </button>
          </div>
        </div>
      )}

      {/* Modal selezione società per nuovo nominativo */}
      {showSocietaModal && (
        <div className="bop-modal-overlay" onClick={(e) => {
          // Non chiudere cliccando sull'overlay
          if (e.target === e.currentTarget) {
            // Non fare nulla - la modale si chiude solo con Annulla
          }
        }}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">In quale società vuoi creare il nominativo?</h2>
            </div>
            <div className="bop-modal__body">
              <p className="bop-nominativi__modal-desc">
                Seleziona la società in cui registrare il nuovo nominativo.
              </p>

              <div className="bop-form-group">
                <label className="bop-label">Società</label>
                <select
                  className="bop-select"
                  value={selectedSocietaForNew}
                  onChange={(e) => setSelectedSocietaForNew(e.target.value)}
                  autoFocus
                >
                  <option value="">Seleziona società...</option>
                  {societaList.map((soc) => (
                    <option key={soc.id} value={soc.id}>
                      {soc.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bop-modal__footer">
              <button
                className="bop-btn bop-btn-secondary"
                onClick={() => {
                  setShowSocietaModal(false);
                  setSelectedSocietaForNew('');
                }}
              >
                Annulla
              </button>
              <button
                className="bop-btn bop-btn-primary"
                onClick={handleContinuaNuovo}
                disabled={!selectedSocietaForNew}
              >
                Continua
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
